import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { BRAND, LOGO } from "@/content/site";
import { LocaleLink as Link } from "@/i18n/routes";

const navItems = [
  { to: "/work", key: "work" },
  { to: "/services", key: "services" },
  { to: "/products", key: "products" },
  { to: "/contact", key: "contact" },
];

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative bg-[#08080A] text-ink border-t border-border overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-bronze/10 blur-[120px]" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 pt-24 md:pt-32 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 pb-20 border-b border-border">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze mb-6">
              {t(BRAND.tagline)}
            </p>
            <Link to="/contact" data-testid="footer-cta" className="group inline-flex items-start gap-3">
              <span className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-ink max-w-3xl">
                {t(UI.home.finalTitle)}
              </span>
            </Link>
            <div className="mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold hover:bg-bronze hover:border-bronze hover:text-paper transition-colors duration-300"
              >
                {t(UI.footer.build)}
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-9">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone mb-5">Menu</p>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    data-testid={`footer-nav-${item.key}`}
                    className="text-sm text-ink/70 hover:text-bronze transition-colors"
                  >
                    {t(UI.nav[item.key])}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone mb-5">Contact</p>
            <ul className="space-y-3 text-sm text-ink/70">
              {BRAND.email && (
                <li>
                  <a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="hover:text-bronze transition-colors break-all">
                    {BRAND.email}
                  </a>
                </li>
              )}
              {BRAND.whatsapp && (
                <li>
                  <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-bronze transition-colors">
                    WhatsApp
                  </a>
                </li>
              )}
            </ul>
            {BRAND.address && (
              <div data-testid="footer-impressum" className="mt-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone mb-3">{t(UI.footer.impressum)}</p>
                <address className="not-italic text-xs leading-relaxed text-stone">
                  {BRAND.owner}<br />
                  {BRAND.address.street}<br />
                  {BRAND.address.city}
                </address>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <div className="flex items-center gap-3">
            <img src={LOGO.badge} alt="WERKVERDUNG" className="h-7 w-7 rounded-[6px] object-cover" />
            <span className="font-display text-xs font-bold tracking-[0.2em]">{BRAND.wordmark}</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/impressum" data-testid="footer-impressum-link" className="font-mono text-[11px] text-stone hover:text-bronze transition-colors">
              Impressum
            </Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="font-mono text-[11px] text-stone hover:text-bronze transition-colors">
              Datenschutz
            </Link>
            <p className="font-mono text-[11px] text-stone">
              © {year} — {t(UI.footer.rights)}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
